import React from 'react';

import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import { noop } from 'utils/noop';

import { ToggleProps } from '.';
import { Palette, Radius, Shadow, Spacing } from '../Theme';

const useStyles = createUseStyles({
    container: { borderRadius: Radius.x50, width: 48, height: 48, position: 'relative', flex: 'none' },
    containerEnabled: {
        cursor: 'pointer',
        '&:hover': { backgroundColor: Palette.Fill.x50 },
        '&:active': { backgroundColor: Palette.Fill.x40 },
    },
    hidden: { opacity: 0, height: 0, width: 0 },
    toggle: {
        borderRadius: Radius.x50,
        display: 'inline-block',
        width: 32,
        height: 20,
        position: 'absolute',
        top: 14,
        left: 8,
        '&::before': {
            transition: 'left 300ms',
            borderRadius: Radius.x50,
            boxShadow: Shadow.x10,
            top: Spacing(0.5),
            width: 12,
            height: 12,
            position: 'absolute',
            content: '""',
            backgroundColor: Palette.Fill.White,
        },
    },
    toggleChecked: { '&::before': { left: Spacing(2), backgroundColor: Palette.Fill.Inverse } },
    toggleUnchecked: { '&::before': { left: Spacing(0.5) } },
    toggleDisabled: { '&::before': { backgroundColor: Palette.Fill.x30 } },
});

const Toggle = React.forwardRef(
    (
        { checked = false, onToggle = noop, disabled = false, inputProps, containerLabelProps }: ToggleProps,
        ref: React.Ref<HTMLInputElement>,
    ): JSX.Element => {
        const classes = useStyles();

        return (
            <label className={clsx(classes.container, !disabled && classes.containerEnabled)} {...containerLabelProps}>
                <input
                    onChange={onToggle}
                    checked={checked}
                    type={'checkbox'}
                    className={classes.hidden}
                    disabled={disabled}
                    ref={ref}
                    {...inputProps}
                />

                <span
                    className={clsx(
                        classes.toggle,
                        checked ? classes.toggleChecked : classes.toggleUnchecked,
                        disabled && classes.toggleDisabled,
                    )}
                    style={{
                        backgroundColor: disabled
                            ? Palette.Fill.x40
                            : checked
                            ? Palette.Brand.Primary
                            : Palette.Fill.x30,
                    }}
                />
            </label>
        );
    },
);

Toggle.displayName = 'Toggle';
export default Toggle;
